import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../api';
import { formatCurrency } from '../utils/currency';
import { useMsal } from '@azure/msal-react';
import {
  CheckCircle,
  CalendarIcon,
  TagIcon,
  BuildingIcon,
  MapPinIcon,
  LayersIcon,
  UserIcon,
  ArrowLeft,
  FileDownIcon,
  UserPlus,
  Send
} from 'lucide-react';
import {
  FaEdit,
  FaCheck,
  FaTimes,
  FaPaperPlane,
  FaRegCommentDots,
  FaImage,
  FaTag,
  FaBuilding,
  FaMapMarkerAlt,
  FaLayerGroup,
  FaCalendarAlt,
  FaUser,
  FaFilePdf,
} from 'react-icons/fa';
import html2pdf from 'html2pdf.js';
import { useRef } from 'react';
import { FaDownload } from 'react-icons/fa';


// Import print CSS for better PDF clarity (Vite will include it automatically)
import '../print-pdf.css';

function ApprovalPage({ userRole }) {
  const userRoles = typeof userRole === 'string' ? userRole.split(',').map(r => r.trim()) : [userRole || 'creator'];
  const hasRole = (role) => userRoles.includes(role);
  const params = useParams();
  const [searchParams] = useSearchParams();
  const rawExpenseId = searchParams.get('expenseId') || params['*'] || params.expenseId || '';
  const expenseId = rawExpenseId.replace(/^\//, '').replace(/\/$/, '').trim();
  const [expense, setExpense] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [replies, setReplies] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFile, setReplyFile] = useState(null); // legacy single
  const [replyFiles, setReplyFiles] = useState([]); // new: multi-files
  const [replyPreview, setReplyPreview] = useState(null);

  const [editingVendor, setEditingVendor] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const [creatorEmail, setCreatorEmail] = useState('');

  const approverEmail = searchParams.get('email');
  const level = searchParams.get('level');
  const userEmail = accounts[0]?.username;
  const [selectedVendorId, setSelectedVendorId] = useState(null); // legacy single select
  const [selectedVendorIds, setSelectedVendorIds] = useState([]); // new: multi-select for Level 1

  useEffect(() => {
    const fetchData = async () => {
      const encodedId = encodeURIComponent(encodeURIComponent(expenseId));
      // Expense
      try {
        let expenseData;
        try {
          const expenseResBy = await api.get(`/api/expenses/by`, { params: { expenseId } });
          expenseData = expenseResBy.data;
        } catch (e1) {
          console.warn('Fallback to path for expense due to:', e1?.response?.data || e1?.message);
          const expenseRes = await api.get(`/api/expenses/${encodedId}`);
          expenseData = expenseRes.data;
        }
        setExpense(expenseData);
        setCreatorEmail(expenseData.creator_email || expenseData.creator_user_id || '');
      } catch (e) {
        console.error('Expense fetch failed:', e?.response?.data || e);
      }

      // Vendors
      try {
        try {
          const vendorsResBy = await api.get(`/api/expenses/vendors/by`, { params: { expenseId } });
          setVendors(vendorsResBy.data);
        } catch (e1) {
          console.warn('Fallback to path for vendors due to:', e1?.response?.data || e1?.message);
          const vendorsRes = await api.get(`/api/expenses/${encodedId}/vendors`);
          setVendors(vendorsRes.data);
        }
      } catch (e) {
        console.error('Vendors fetch failed:', e?.response?.data || e);
      }

      // Line Items (for approver view)
      try {
        const itemsRes = await api.get(`/api/expenses/${encodedId}/items`);
        setLineItems(itemsRes.data || []);
      } catch (e) {
        console.error('Line items fetch failed:', e?.response?.data || e);
      }

      // History
      try {
        try {
          const historyRes = await api.get(`/api/approvals/history/by`, { params: { expenseId } });
          setHistory(historyRes.data);
        } catch (e1) {
          console.warn('Fallback to path for history due to:', e1?.response?.data || e1?.message);
          const historyRes2 = await api.get(`/api/approvals/${encodedId}/history`);
          setHistory(historyRes2.data);
        }
      } catch (e) {
        console.error('History fetch failed:', e?.response?.data || e);
      }

      // Replies
      try {
        try {
          const repliesRes = await api.get(`/api/approvals/replies/by`, { params: { expenseId } });
          setReplies(repliesRes.data);
        } catch (e1) {
          console.warn('Fallback to path for replies due to:', e1?.response?.data || e1?.message);
          const repliesRes2 = await api.get(`/api/approvals/${encodedId}/replies`);
          setReplies(repliesRes2.data);
        }
      } catch (e) {
        console.error('Replies fetch failed:', e?.response?.data || e);
      }
      setLoading(false);
    };

    fetchData();
  }, [expenseId]);

  const handleAction = async (decision) => {
    try {
      const account = accounts[0];
      if (!account) {
        alert("User is not signed in. Please sign in again.");
        return;
      }

      const signedInEmail = account.username;

      const response = await instance.acquireTokenSilent({
        scopes: ["Mail.Send", "User.Read"],
        account,
      });

      const freshToken = response.accessToken;

      const encodedId = encodeURIComponent(encodeURIComponent(expenseId));
      await api.post(
        `/api/approvals/${encodedId}`,
        {
          approverEmail: signedInEmail,
          accessToken: freshToken,
          decision,
          comment,
          level: Number(level),
          selectedVendorId: selectedVendorId ? parseInt(selectedVendorId) : null,
          selectedVendorIds: Array.isArray(selectedVendorIds) ? selectedVendorIds.map(id => parseInt(id)) : [],
        }
      );

      alert(`Expense ${decision}d`);
      window.location.reload();
    } catch (err) {
      console.error("Error updating status:", err);

      if (err.errorMessage?.includes("interaction_required")) {
        try {
          const popupResponse = await instance.acquireTokenPopup({
            scopes: ["Mail.Send", "User.Read"],
          });

          const freshToken = popupResponse.accessToken;
          const signedInEmail = accounts[0].username;

          const encodedId2 = encodeURIComponent(encodeURIComponent(expenseId));
          await api.post(
            `/api/approvals/${encodedId2}`,
            {
              approverEmail: signedInEmail,
              accessToken: freshToken,
              decision,
              comment,
              level: Number(level),
              selectedVendorId: selectedVendorId ? parseInt(selectedVendorId) : null,
              selectedVendorIds: Array.isArray(selectedVendorIds) ? selectedVendorIds.map(id => parseInt(id)) : [],
            }
          );

          alert(`Expense ${decision}d`);
          window.location.reload();
        } catch (popupErr) {
          console.error("Token refresh failed via popup:", popupErr);
          alert("Failed to get a valid access token. Please sign in again.");
        }
      } else {
        alert("Action failed. Check console for details.");
      }
    }
  };

  const handleReplySubmit = async () => {
    if (!replyMessage.trim() && !replyFile) {
      alert('Please add a message or attach a file.');
      return;
    }

    try {
      if (!activeAccount) {
        alert("User not authenticated. Please log in again.");
        return;
      }

      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["Mail.Send", "User.Read", "Files.ReadWrite.All"],
        account: activeAccount,
      });

      const encodedReplyId = encodeURIComponent(encodeURIComponent(expenseId));
      const form = new FormData();
      form.append('reply_by', userEmail);
      form.append('reply_to_level', level || 1);
      form.append('message', replyMessage || '');
      form.append('accessToken', tokenResponse.accessToken);
      if (replyFiles && replyFiles.length > 0) {
        replyFiles.forEach(f => form.append('attachments', f));
      } else if (replyFile) {
        // fallback: still send under new field name for backend compatibility
        form.append('attachments', replyFile);
      }
      // Primary: include raw expenseId in form for any backend that reads from body
      form.append('expenseId', expenseId);
      // Let Axios set the multipart boundary automatically
      const attempts = [
        [`/api/approvals/${encodedReplyId}/reply`, true],
      ];
      let posted = false;
      for (const [url] of attempts) {
        try {
          await api.post(url, form);
          posted = true;
          break;
        } catch (e) {
          // No other valid POST endpoints exist; surface the first error
          throw e;
        }
      }
      if (!posted) {
        throw new Error('No compatible reply endpoint found after trying multiple patterns.');
      }

      alert("✅ Comment sent and email delivered successfully!");
      setReplyMessage("");
      setReplyFile(null);
      setReplyFiles([]);
      setReplyPreview(null);

      try {
        const repliesRes = await api.get(`/api/approvals/replies/by`, { params: { expenseId } });
        setReplies(repliesRes.data);
      } catch (e) {
        const repliesRes2 = await api.get(`/api/approvals/${encodedReplyId}/replies`);
        setReplies(repliesRes2.data);
      }
    } catch (err) {
      console.error("❌ Error posting reply:", err?.response?.data || err);
      alert("❌ Failed to post reply or send email");
    }
  };

  const handleReplyFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    // Append to existing files; de-dupe by name+size
    setReplyFiles((prev) => {
      const map = new Map((prev || []).map(f => [f.name + ':' + f.size, f]));
      for (const f of files) {
        map.set(f.name + ':' + f.size, f);
      }
      return Array.from(map.values());
    });
    const first = files[0] || replyFiles?.[0] || null;
    setReplyFile(first); // keep legacy single for UI display
    if (first && first.type && first.type.startsWith('image/')) {
      const url = URL.createObjectURL(first);
      setReplyPreview(url);
    } else {
      setReplyPreview(null);
    }
  };

  const handleClearAttachment = () => {
    setReplyFile(null);
    setReplyFiles([]);
    setReplyPreview(null);
    const input = document.getElementById('reply-attachment-input');
    if (input) input.value = '';
  };

  const handleEditClick = (vendor) => {
    setEditingVendor({ ...vendor });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingVendor((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async () => {
    try {
      // Use vendor-only PUT to avoid expenseId with slashes in the path
      await api.put(`/api/expenses/vendors/${editingVendor.id}`, editingVendor);
      const encodedId = encodeURIComponent(encodeURIComponent(expenseId));
      const vendorsRes = await api.get(`/api/expenses/${encodedId}/vendors`);
      setVendors(vendorsRes.data);
      setShowEditModal(false);
    } catch (err) {
      console.error('Failed to update vendor:', err);
      alert('Update failed');
    }
  };

  const pdfRef = useRef(null);

  const handleExportPDF = () => {
    const element = document.getElementById('pdf-export-root') || pdfRef.current;
    if (!element) {
      console.error('PDF root not found');
      alert('Unable to prepare PDF. Please try again.');
      return;
    }
    element.style.display = 'block';
    const options = {
      margin: 0.2,
      filename: `Expense_Report_${expense.expense_id}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 4, useCORS: true, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' }
    };
    // Let browser render before capture
    setTimeout(() => {
      html2pdf().set(options).from(element).save().then(() => {
        element.style.display = 'none';
      }).catch(() => {
        element.style.display = 'none';
      });
    }, 0);
  };

  if (loading) return <div className="p-6 flex justify-center items-center h-screen">Loading...</div>;

  const budgetColorClass = expense?.category_budget?.remaining_amount < 0
    ? 'bg-red-100 text-red-800'
    : 'bg-blue-100 text-blue-800';

  // Normalize status text for display
  const prettyStatus = (s) => {
    const v = String(s || '').trim();
    const lower = v.toLowerCase();
    if (lower === 'approved') return 'Approved';
    if (lower === 'rejected') return 'Rejected';
    if (lower.startsWith('pending')) {
      // Preserve any suffix like " Level 1"
      return 'Pending' + v.slice(7);
    }
    return v;
  };

  // Expected approvers per department (for display correction only)
  const isRecurring = expense?.recurring_type === 'Recurring Expense' || (expense?.recurring_type || '').toString().trim().toLowerCase().includes('recurring');
  const dept = String(expense?.department_id || '').toLowerCase();
  const expectedApprovers = isRecurring
    ? { 1: 'Kishor.Dholwani@acornuniversalconsultancy.com' }
    : (dept.includes('it')
      ? { 1: 'Pragnesh@astutehealthcare.co.uk', 2: 'Chetan@astutehealthcare.co.uk', 3: 'Kishor.Dholwani@acornuniversalconsultancy.com' }
      : { 1: 'Kishor.Dholwani@acornuniversalconsultancy.com', 2: 'Chetan@astutehealthcare.co.uk', 3: 'Pragnesh@astutehealthcare.co.uk' });

  // Derived: If Level 3 shows Approved in history (or Level 1 for Recurring), treat overall as Approved for display
  const l3Approved = Array.isArray(history) && history.some(h => Number(h.level) === 3 && String(h.status || '').trim().toLowerCase() === 'approved');
  const recurringApproved = isRecurring && Array.isArray(history) && history.some(h => Number(h.level) === 1 && String(h.status || '').trim().toLowerCase() === 'approved');
  const displayStatus = (l3Approved || recurringApproved) ? 'Approved' : expense?.status;

  // Determine if a vendor has already been locked/selected by any approver (ideally Level 1)
  const firstWithVendor = history?.find(h => !!h.selected_vendor_name);
  const vendorLocked = !!firstWithVendor;
  const lockedVendorName = firstWithVendor?.selected_vendor_name || null;
  const currentLevel = Number(level) || 0;

  // Determine if the signed-in approver has already acted on this expense
  const myHistoryRow = history?.find(
    (h) => String(h.approver_email || '').toLowerCase() === String(userEmail || '').toLowerCase()
  );
  const hasApprovedByMe = myHistoryRow?.status === 'Approved';
  const hasRejectedByMe = myHistoryRow?.status === 'Rejected';
  const hasActedByMe = hasApprovedByMe || hasRejectedByMe;

  return (
    <div className="min-h-screen p-6 flex justify-center" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-5 gap-6">
        {/* Left Column (Main Content) */}
        <div className="md:col-span-3 space-y-6">
          {/* PDF Header: Always included in capture */}
          <div className="rounded-xl shadow-sm p-4 border flex items-center justify-between" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
            <div>
              <div className="text-lg font-bold" style={{ color: 'var(--text)' }}>Expense Approval: {expenseId}</div>
              <div className="text-sm" style={{ color: 'var(--muted)' }}>Expense Title: {expense?.expense_name}</div>
            </div>
            <button
              onClick={handleExportPDF}
              className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm font-semibold"
              style={{ background: '#8c211e', color: '#ffffff', borderColor: '#721a18' }}
              title="Export PDF"
            >
              <FaDownload className="w-4 h-4" />
              Export PDF
            </button>
          </div>
          <div className="rounded-xl shadow-sm p-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
            <h3 className="text-sm font-light text-gray-500 mb-6">Review and approve expense request</h3>

            {/* Expense Details - New design */}
            <div className="rounded-lg p-6 mb-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
              <h2 className="text-lg font-semibold mb-4">Expense Details</h2>
              <div className="space-y-4 text-sm">
                <div className="flex items-center space-x-2">
                  <FaTag className="text-gray-500" />
                  <span><strong>Category:</strong> {expense?.expense_category}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaBuilding className="text-gray-500" />
                  <span><strong>Department:</strong> {expense?.department_id}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaMapMarkerAlt className="text-gray-500" />
                  <span><strong>Region:</strong> {expense?.region_id}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaLayerGroup className="text-gray-500" />
                  <span><strong>Group:</strong> {expense?.expense_group}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaCalendarAlt className="text-gray-500" />
                  <span><strong>Date:</strong> {new Date(expense?.date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaUser className="text-gray-500" />
                  <span><strong>Created By:</strong> {expense?.creator_user_id}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-gray-700">Status:</span>
                  <span
                    className="px-2 py-1 rounded-full text-xs font-semibold"
                    style={{
                      background:
                        String(displayStatus || '').toLowerCase() === 'approved' ? 'rgba(34,197,94,0.15)'
                          : String(displayStatus || '').toLowerCase() === 'rejected' ? 'rgba(239,68,68,0.15)'
                            : String(displayStatus || '').toLowerCase().startsWith('pending') ? 'rgba(234,179,8,0.18)'
                              : 'rgba(148,163,184,0.16)',
                      color:
                        String(displayStatus || '').toLowerCase() === 'approved' ? '#22c55e'
                          : String(displayStatus || '').toLowerCase() === 'rejected' ? '#ef4444'
                            : String(displayStatus || '').toLowerCase().startsWith('pending') ? '#a16207'
                              : 'var(--muted)'
                    }}
                  >
                    {prettyStatus(displayStatus)}
                  </span>
                </div>
              </div>

              {expense?.category_budget && (
                <div className={`mt-6 p-3 rounded-md text-sm ${budgetColorClass}`}>
                  <p className="font-semibold">Budget Information:</p>
                  <p>Remaining budget for <strong className="font-bold">{expense.category_budget.category_name}</strong>: ₹{expense.category_budget.remaining_amount} of ₹{expense.category_budget.budget_amount}</p>
                </div>
              )}
            </div>

            {/* Vendor Details */}
            <div className="rounded-lg p-6 mb-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
              <h2 className="text-lg font-semibold mb-4">Vendor Details</h2>
              <h3 className="text-sm font-light text-gray-500 mb-4">Review vendor proposals and costs</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full" style={{ color: 'var(--text)' }}>
                  <thead className="border-b" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--muted)' }}>
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Terms</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attachment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.map((v) => (
                      <tr key={v.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{v.vendor_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{formatCurrency(v.cost, v.currency || 'INR')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{new Date(v.date).toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{v.payment_terms}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{v.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--accent)' }}>
                          {v.attachment_url ? (
                            <a href={v.attachment_url} target="_blank" rel="noreferrer" className="hover:underline">View</a>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Per-vendor line items below each vendor */}
              <div className="space-y-4 mt-4">
                {vendors.map((v) => {
                  const items = lineItems.filter(li => Number(li.vendor_detail_id || 0) === Number(v.id));
                  const vendorTotal = items.reduce((s, li) => s + ((Number(li.ordered_qty) || 0) * (Number(li.unit_price) || 0)), 0);
                  return (
                    <div key={`items-${v.id}`} className="rounded border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
                      <div className="px-4 py-2 text-sm font-semibold">Items for: {v.vendor_name}</div>
                      {items.length === 0 ? (
                        <div className="px-4 pb-3 text-sm" style={{ color: 'var(--muted)' }}>No line items for this vendor.</div>
                      ) : (
                        <div className="overflow-x-auto px-4 pb-3">
                          <table className="min-w-full text-sm">
                            <thead className="border-b" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--muted)' }}>
                              <tr>
                                <th className="p-2 text-left">Type</th>
                                <th className="p-2 text-left">Item</th>
                                <th className="p-2 text-left">UOM</th>
                                <th className="p-2 text-right">Qty</th>
                                <th className="p-2 text-right">Unit Price</th>
                                <th className="p-2 text-right">Line Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((li, idx) => {
                                const name = li.type === 'Inventory' ? `${li.item_name || ''}${li.item_sku ? ` (${li.item_sku})` : ''}` : (li.free_text_name || '');
                                const qty = Number(li.ordered_qty) || 0;
                                const price = Number(li.unit_price) || 0;
                                const total = qty * price;
                                return (
                                  <tr key={idx} className="border-t" style={{ borderColor: 'var(--border)' }}>
                                    <td className="p-2">{li.type}</td>
                                    <td className="p-2">{name}</td>
                                    <td className="p-2">{li.uom}</td>
                                    <td className="p-2 text-right">{qty}</td>
                                    <td className="p-2 text-right">{formatCurrency(price, expense?.currency || 'INR')}</td>
                                    <td className="p-2 text-right">{formatCurrency(total, expense?.currency || 'INR')}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td className="p-2 font-semibold" colSpan={5} style={{ textAlign: 'right' }}>Subtotal</td>
                                <td className="p-2 font-semibold text-right">{formatCurrency(vendorTotal, expense?.currency || 'INR')}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Show locked vendor summary if already selected by Level 1 */}
              {vendorLocked && (
                <div className="mt-4 p-3 rounded-md text-sm" style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)', color: '#1e3a8a' }}>
                  <span className="font-semibold">Selected Vendor (locked):</span>{' '}
                  <span>{lockedVendorName}</span>
                </div>
              )}

              {/* Only Level 1 can select vendors and only if not already locked (multi-select) */}
              {!hasRole('creator') && vendors.length > 0 && currentLevel === 1 && !vendorLocked && (
                <div className="mt-6 p-4 rounded-lg" style={{ background: 'var(--panel)', border: '1px solid var(--border)' }}>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Select Vendors for Approval (multiple allowed)</h3>
                  <div className="space-y-2">
                    {vendors.map(v => {
                      const checked = selectedVendorIds.includes(v.id) || selectedVendorIds.includes(String(v.id));
                      return (
                        <label key={v.id} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={!!checked}
                            onChange={(e) => {
                              const val = String(v.id);
                              setSelectedVendorIds(prev => {
                                const set = new Set(prev.map(String));
                                if (e.target.checked) set.add(val); else set.delete(val);
                                return Array.from(set);
                              });
                            }}
                          />
                          <span className="text-gray-800">{v.vendor_name} - {formatCurrency(v.cost, v.currency || 'INR')}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">You must select at least one vendor to proceed.</p>
                </div>
              )}
            </div>

            {/* Approval History */}
            <div className="rounded-lg p-6 mb-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
              <h2 className="text-lg font-semibold mb-4">Approval History</h2>
              <h3 className="text-sm font-light mb-4" style={{ color: 'var(--muted)' }}>Track the approval process</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full" style={{ color: 'var(--text)' }}>
                  <thead className="border-b" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--muted)' }}>
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Level</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Approver</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Comment</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Selected Vendor</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Action Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, idx) => (
                      <tr key={idx} className="border-b" style={{ borderColor: 'var(--border)' }}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--muted)' }}>{h.level}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{h.approver_email || expectedApprovers[h.level] || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${(String(h?.status || '').toLowerCase() === 'approved')
                              ? 'bg-green-100 text-green-800'
                              : (String(h?.status || '').toLowerCase() === 'rejected')
                                ? 'bg-red-100 text-red-800'
                                : String(h?.status || '').toLowerCase().startsWith('pending')
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                          >
                            {prettyStatus(h.status) || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--muted)' }}>{h.comment || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--muted)' }}>{h.selected_vendor_name || "-"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--muted)' }}>{h.action_date ? new Date(h.action_date).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (Action & Discussion) */}
        <div className="md:col-span-2 space-y-6">

          {/* Discussion Card */}
          <div className="rounded-xl shadow-sm p-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
            <h3 className="text-xl font-semibold mb-1 flex items-center space-x-2">
              <FaRegCommentDots className="text-gray-500" />
              <span>Discussion</span>
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>Comments and replies from team members</p>

            {/* Replies List */}
            <div className="space-y-4">
              {replies.map((r, index) => {
                const rawAttachment = r.attachment_url || r.attachmentUrl || r.attachment || r.fileUrl;
                const urls = typeof rawAttachment === 'string' && rawAttachment.includes('|')
                  ? rawAttachment.split('|').map(s => (s || '').trim()).filter(Boolean)
                  : (rawAttachment ? [rawAttachment] : []);
                return (
                  <div key={index} className="p-3 border rounded-md text-sm" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
                    <p className="font-semibold break-words" style={{ color: 'var(--text)' }}>
                      {r.reply_by}
                    </p>
                    <p className="my-1" style={{ color: 'var(--text)' }}>{r.message}</p>
                    {urls.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {urls.map((u, i) => (
                          <a
                            key={i}
                            className="underline break-all block"
                            style={{ color: 'var(--accent)' }}
                            href={u}
                            target="_blank"
                            rel="noreferrer"
                            title="Open attachment"
                          >
                            {`View file${urls.length > 1 ? ` ${i + 1}` : ''}`}
                          </a>
                        ))}
                      </div>
                    )}
                    <p className="text-xs mt-2" style={{ color: 'var(--muted)' }}>{new Date(r.reply_date).toLocaleString()}</p>
                  </div>
                )
              })}
            </div>

            {/* Reply Input */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-2">Your Reply</h3>
              <textarea
                className="w-full h-20 p-3 rounded-md resize-none focus:outline-none"
                style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                placeholder="Type your message here..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
              ></textarea>
              <div className="mt-2 flex items-start gap-3">
                {/* Hidden native input */}
                <input
                  id="reply-attachment-input"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,image/*"
                  multiple
                  onChange={handleReplyFileChange}
                  className="sr-only"
                />
                {/* Branded button with gallery icon */}
                <label
                  htmlFor="reply-attachment-input"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-md cursor-pointer"
                  style={{ color: 'var(--text)', border: '1px solid var(--border)' }}
                >
                  <FaImage />
                </label>
                {/* PDF attach button (uses same input) */}
                <label
                  htmlFor="reply-attachment-input"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-md cursor-pointer"
                  style={{ color: 'var(--text)', border: '1px solid var(--border)' }}
                  title="Attach PDF"
                >
                  <FaFilePdf />
                </label>
                <div className="text-xs" style={{ color: 'var(--muted)' }}>
                  PDF, JPG, PNG allowed
                </div>
                {replyPreview && (
                  <div className="border rounded p-1" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
                    <img src={replyPreview} alt="preview" className="h-16 w-16 object-cover rounded" />
                  </div>
                )}
                {(replyFiles && replyFiles.length > 0 && !replyPreview) && (
                  <span className="text-xs break-all" style={{ color: 'var(--muted)' }}>{replyFiles.length === 1 ? replyFiles[0].name : `${replyFiles.length} files selected`}</span>
                )}
                {replyFile && (
                  <button
                    type="button"
                    onClick={handleClearAttachment}
                    className="text-xs px-2 py-1 rounded-full"
                    style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <button
                className="mt-3 w-full flex items-center justify-center font-bold py-3 px-6 rounded-md transition-colors border"
                style={{ background: '#8c211e', color: '#ffffff', borderColor: '#721a18' }}
                onClick={handleReplySubmit}
              >
                <FaPaperPlane className="mr-2" /> Submit Reply
              </button>
            </div>
          </div>
          {/* Take Action Card */}
          {!hasRole('creator') && (
            <div className="rounded-xl shadow-sm p-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
              <h3 className="text-xl font-semibold mb-1">Take Action</h3>
              <p className="text-sm text-gray-500 mb-4">Approve or reject this expense request</p>

              <div className="mb-4">
                <label htmlFor="comments" className="block text-sm font-semibold text-gray-700 mb-2">Comments (optional)</label>
                <textarea
                  id="comments"
                  className="w-full h-20 p-3 rounded-md resize-none focus:outline-none"
                  style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                  placeholder="Add your comments here..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
              {/* Info banner when user has already acted */}
              {hasActedByMe && (
                <div className={`mb-4 p-3 rounded-md border text-sm ${hasApprovedByMe
                  ? 'bg-green-100 border-green-200 text-green-800'
                  : 'bg-red-100 border-red-200 text-red-800'
                  }`}>
                  <span className="font-semibold">
                    {hasApprovedByMe ? 'You already approved' : 'You already rejected'}
                  </span>
                  {` this expense${myHistoryRow?.level ? ` at Level ${myHistoryRow.level}` : ''}`}
                  {` on ${myHistoryRow?.action_date ? new Date(myHistoryRow.action_date).toLocaleString() : '—'}.`}
                </div>
              )}
              <div className="flex space-x-3">
                {(!vendorLocked && currentLevel > 1) && (
                  <div className="flex-1 text-sm text-red-600 border border-red-200 rounded-md p-3 mr-3">
                    Waiting for Level 1 to select a vendor.
                  </div>
                )}
                <button
                  className={`flex-1 flex items-center justify-center ${hasActedByMe
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : (!vendorLocked && currentLevel === 1 && (selectedVendorIds.length === 0) && !hasRole('creator'))
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-green-200 text-green-700 hover:bg-green-300'
                    } font-bold py-3 px-6 rounded-md transition-colors`}
                  onClick={() => handleAction('approve')}
                  disabled={
                    hasActedByMe ||
                    (!vendorLocked && currentLevel === 1 && (selectedVendorIds.length === 0) && !hasRole('creator')) ||
                    (!vendorLocked && currentLevel > 1)
                  }
                  title={hasApprovedByMe ? 'You already approved this expense' : hasRejectedByMe ? 'You already rejected this expense' : undefined}
                >
                  <CheckCircle className="mr-2" /> Approve
                </button>
                <button
                  className={`flex-1 flex items-center justify-center ${hasActedByMe
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-red-200 text-red-700 hover:bg-red-300'
                    } font-bold py-3 px-6 rounded-md transition-colors`}
                  onClick={() => handleAction('reject')}
                  disabled={hasActedByMe}
                  title={hasActedByMe ? 'You already acted on this expense' : undefined}
                >
                  <FaTimes className="mr-2" /> Reject
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hidden PDF Export Container (not visible in UI) */}
      <div
        ref={pdfRef}
        id="pdf-export-root"
        className="pdf-only"
        style={{
          display: 'none',
          background: '#ffffff',
          color: '#111827',
          '--text': '#111827',
          '--muted': '#374151',
          '--panel': '#ffffff',
          '--border': '#e5e7eb'
        }}
      >
        <div className="p-8">
          <div className="mb-4">
            <h1 className="text-2xl font-bold">Expense Report</h1>
            <p className="text-sm">Expense ID: <span className="font-semibold">{expenseId}</span></p>
            <p className="text-sm">Expense Title: <span className="font-semibold">{expense?.expense_name || '-'}</span></p>
          </div>
          {/* Expense Details Section */}
          <div className="border rounded-lg p-6 mb-6" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)' }}>Expense Details</h2>
            <div className="space-y-4 text-sm" style={{ color: 'var(--text)' }}>
              <div className="flex items-center space-x-2"><FaTag style={{ color: 'var(--muted)' }} /><span><strong>Category:</strong> {expense?.expense_category}</span></div>
              <div className="flex items-center space-x-2"><FaBuilding style={{ color: 'var(--muted)' }} /><span><strong>Department:</strong> {expense?.department_id}</span></div>
              <div className="flex items-center space-x-2"><FaMapMarkerAlt style={{ color: 'var(--muted)' }} /><span><strong>Region:</strong> {expense?.region_id}</span></div>
              <div className="flex items-center space-x-2"><FaLayerGroup style={{ color: 'var(--muted)' }} /><span><strong>Group:</strong> {expense?.expense_group}</span></div>
              <div className="flex items-center space-x-2"><FaCalendarAlt style={{ color: 'var(--muted)' }} /><span><strong>Date:</strong> {expense?.date ? new Date(expense.date).toLocaleDateString() : '-'}</span></div>
              <div className="flex items-center space-x-2"><FaUser style={{ color: 'var(--muted)' }} /><span><strong>Created By:</strong> {expense?.creator_user_id}</span></div>
              <div className="flex items-center space-x-2"><span className="font-bold" style={{ color: 'var(--text)' }}>Status:</span><span className={`px-2 py-1 rounded-full text-xs font-semibold ${String(displayStatus || '').toLowerCase() === 'approved' ? 'bg-green-100 text-green-800' :
                String(displayStatus || '').toLowerCase() === 'rejected' ? 'bg-red-100 text-red-800' :
                  String(displayStatus || '').toLowerCase().startsWith('pending') ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                }`}>{prettyStatus(displayStatus)}</span></div>
            </div>
            {expense?.category_budget && (
              <div className={`mt-6 p-3 rounded-md text-sm ${expense?.category_budget?.remaining_amount < 0 ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                <p className="font-semibold">Budget Information:</p>
                <p>Remaining budget for <strong className="font-bold">{expense.category_budget.category_name}</strong>: ₹{expense.category_budget.remaining_amount} of ₹{expense.category_budget.budget_amount}</p>
              </div>
            )}
          </div>
          {/* Vendor + Approval History Group for PDF */}
          <div>
            {/* Vendor Details Section */}
            <div className="border rounded-lg p-6 mb-6" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)' }}>Vendor Details</h2>
              <h3 className="text-sm font-light mb-4" style={{ color: 'var(--muted)' }}>Review vendor proposals and costs</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y" style={{ borderColor: 'var(--border)' }}>
                  <thead style={{ background: 'var(--panel-secondary, var(--panel))' }}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Cost</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Payment Terms</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Attachment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
                    {vendors.map((v) => (
                      <tr key={v.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>{v.vendor_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>₹{v.cost}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>{v.date ? new Date(v.date).toLocaleDateString() : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>{v.payment_terms}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>{v.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">{v.attachment_url ? (<a href={v.attachment_url} target="_blank" rel="noreferrer" className="hover:underline">View</a>) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Approval History Section */}
            <div className="border rounded-lg p-6 mb-6" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)' }}>Approval History</h2>
              <h3 className="text-sm font-light mb-4" style={{ color: 'var(--muted)' }}>Track the approval process</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y" style={{ borderColor: 'var(--border)' }}>
                  <thead style={{ background: 'var(--panel-secondary, var(--panel))' }}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Level</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Approver</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Comment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Selected Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Action Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
                    {history.map((h, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>{h.level}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>{h.approver_email || expectedApprovers[h.level] || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold" style={{ color: 'var(--text)' }}>{prettyStatus(h.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>{h.comment || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{h.selected_vendor_name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{h.action_date ? new Date(h.action_date).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* PDF Page Break before Discussion */}
          <div className="pdf-page-break" />
          {/* Discussion Section */}
          {replies && replies.length > 0 && (
            <div className="rounded-xl shadow-md p-6 mb-6" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
              <h3 className="text-xl font-semibold mb-1 flex items-center space-x-2" style={{ color: 'var(--text)' }}>
                <FaRegCommentDots style={{ color: 'var(--muted)' }} />
                <span>Discussion</span>
              </h3>
              <div className="space-y-4">
                {replies.map((r, index) => (
                  <div key={index} className="bg-gray-50 p-3 border border-gray-200 rounded-md text-sm">
                    <p className="font-semibold text-gray-800 break-words">{r.reply_by}</p>
                    <p className="text-gray-600 my-1">{r.message}</p>
                    <p className="text-xs text-gray-400 mt-2">{r.reply_date ? new Date(r.reply_date).toLocaleString() : '-'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expense Items */}
          <div className="rounded-lg p-6 mb-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
            <h2 className="text-lg font-semibold mb-4">Expense Items</h2>
            <h3 className="text-sm font-light text-gray-500 mb-4">Items included in this expense</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full" style={{ color: 'var(--text)' }}>
                <thead className="border-b" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--muted)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Item</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">UOM</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Unit Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                  {expense?.items?.length > 0 ? (
                    expense.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.item_name || item.free_text_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.type || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.uom || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.ordered_qty ? Number(item.ordered_qty).toFixed(2) : '0.00'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.unit_price ? formatCurrency(item.unit_price) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {item.total_price ? formatCurrency(item.total_price) : 'N/A'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm" style={{ color: 'var(--muted)' }}>
                        No items found for this expense.
                      </td>
                    </tr>
                  )}
                </tbody>
                {expense?.items?.length > 0 && (
                  <tfoot className="border-t" style={{ borderColor: 'var(--border)' }}>
                    <tr>
                      <td colSpan="5" className="px-6 py-3 text-right text-sm font-medium">
                        Total Amount:
                      </td>
                      <td className="px-6 py-3 text-sm font-medium">
                        {formatCurrency(expense.items.reduce((sum, item) => sum + (Number(item.total_price) || 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
          )&rbrace;
        </div>
      </div>
    </div>
  );
}

export default ApprovalPage;